/**
 * Service Type Service
 * 
 * Handles API calls for service types data
 */

import { getApiClient } from './client';
import type { ServiceType, ServiceTypeListResponse, ServiceTypeResponse } from '@/types/service.types';

/**
 * Type guard to validate ServiceType structure
 */
function isServiceType(obj: unknown): obj is ServiceType {
  if (typeof obj !== 'object' || obj === null) return false;
  
  const st = obj as Record<string, unknown>;
  
  return (
    typeof st.companyId === 'number' &&
    typeof st.donationType === 'number'
  );
}

/**
 * Type guard to validate ServiceTypeListResponse structure
 */
function isServiceTypeListResponse(obj: unknown): obj is ServiceTypeListResponse {
  if (typeof obj !== 'object' || obj === null) return false;
  
  const response = obj as Record<string, unknown>;
  
  return (
    Array.isArray(response.items) &&
    response.items.every(isServiceType) &&
    typeof response.count === 'number'
  );
}

/**
 * Type guard to validate ServiceTypeResponse structure
 */
function isServiceTypeResponse(obj: unknown): obj is ServiceTypeResponse {
  if (typeof obj !== 'object' || obj === null) return false;
  
  const response = obj as Record<string, unknown>;
  
  return (
    typeof response.item === 'object' &&
    response.item !== null &&
    isServiceType(response.item)
  );
}

/**
 * Fetch service types for a specific company (web-enabled only)
 * 
 * @param companyId - The company ID to fetch service types for
 * @returns Promise with service types list
 * @throws Error if request fails or response is invalid
 */
export async function getServiceTypes(companyId: string | number): Promise<ServiceTypeListResponse> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<ServiceTypeListResponse>(
      `/donationtypes/${companyId}?statusCode=2&showWeb=1&taxReceipt=0`
    );

    // Validate response structure
    if (!isServiceTypeListResponse(response.data)) {
      throw new Error('Invalid response structure from service types API');
    }

    return response.data;
  } catch (error) {
    // Re-throw with additional context
    if (error instanceof Error) {
      throw new Error(`Failed to fetch service types: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Fetch a single service type by ID
 * 
 * @param companyId - The company ID
 * @param serviceTypeId - The service type ID
 * @returns Promise with service type details
 * @throws Error if request fails or response is invalid
 */
export async function getServiceType(
  companyId: string | number,
  serviceTypeId: number
): Promise<ServiceType> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<ServiceTypeResponse>(
      `/servicetypes/${companyId}/${serviceTypeId}`
    );

    if (!isServiceTypeResponse(response.data)) {
      throw new Error('Invalid response structure from service type API');
    }

    return response.data.item;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch service type: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Parse suggested amounts from CSV string
 * 
 * @param suggestedAmounts - CSV string of amounts
 * @returns Array of numbers
 */
export function parseSuggestedAmounts(suggestedAmounts: string | null): number[] {
  if (!suggestedAmounts) return [];
  
  return suggestedAmounts
    .split(',')
    .map((amount) => parseFloat(amount.trim()))
    .filter((amount) => !isNaN(amount) && amount > 0);
}
